/**
 * Talking to the API.
 *
 * Small on purpose. The one decision worth commenting is the error shape: a refusal from this API
 * carries `{ error, hint }`, and both are surfaced. Screens that swallow the hint and render
 * "Something went wrong" throw away the half of the message that tells somebody what to do.
 */

import { useCallback, useEffect, useState } from "react";

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly hint?: string) {
    super(message);
  }
}

/**
 * The development identity headers — in the DEV build only.
 *
 * `import.meta.env.DEV` is a compile-time constant: Vite replaces it with `false` in a production
 * build and the whole branch, header names included, is dropped from the bundle (`build.test.ts`
 * reads the built output and asserts exactly that). So the deployed SPA never sends these, and the
 * API — which refuses them in production anyway — is not the only thing standing between a dev
 * shim and a production request. In development they are read from localStorage so a reviewer can
 * switch tenant, identity or role without a rebuild; the role is under the same table as a real
 * session, so an absent `plat_dev_role` would be VIEWER — ADMIN is the local default.
 * `plat_dev_off` = "1" sends none of them, so the sign-in screen, a real session cookie and the
 * sign-out that clears it can be exercised in development (the end-to-end suite does exactly that).
 */
function devHeaders(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  const read = (key: string): string | null => { try { return localStorage.getItem(key); } catch { return null; } };
  if (read("plat_dev_off") === "1") return {};
  return {
    "x-plat-dev-tenant": read("plat_dev_tenant") ?? "acme",
    "x-plat-dev-identity": read("plat_dev_identity") ?? "operator",
    "x-plat-dev-role": read("plat_dev_role") ?? "ADMIN",
  };
}

/** Fired on the window when any call answers 401, so the shell can show the sign-in screen. */
export const UNAUTHENTICATED_EVENT = "plat:unauthenticated";

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...devHeaders(),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { error: text }; }

  if (!res.ok) {
    const b = body as { error?: string; hint?: string };
    if (res.status === 401 && typeof window !== "undefined") window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    throw new ApiError(res.status, b.error ?? `Request failed (${res.status}).`, b.hint);
  }
  return body as T;
}

export interface Loaded<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Load once, and expose the three states separately.
 *
 * `loading`, `error` and `data` are distinct rather than collapsed into `data ?? []`, because an
 * empty list and a failed request look identical on screen when they are collapsed.
 */
export function useApi<T>(path: string | null, deps: unknown[] = []): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) { setLoading(false); return; }
    let live = true;
    setLoading(true);
    setError(null);
    api<T>(path)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
        setLoading(false);
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, error, loading, reload };
}

export function relative(iso: string): string {
  const then = Date.parse(iso);
  const mins = Math.round((Date.now() - then) / 60_000);
  if (Number.isNaN(mins)) return iso;
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
